// #region Imports
import { styled } from "@mui/material/styles";
import { SearchMatchScope } from "_shared/_views/tables/search-match";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import { useLoadPastFilteredRows } from "_shared/_hooks/infinite-scroll/use-load-past-filtered-rows";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { MRTNoMemo } from "_shared/_views/tables/table";
import {
  ROW_CHOICE_CELL_PROPS,
  ROW_CHOICE_TABLE_OPTIONS,
  RowChoiceButton,
  RowChoicePerson,
  rowChoiceRowProps,
  type RowChoiceUnavailableReason,
} from "_shared/_views/tables/row-choice";
import {
  isInExcludedLegalEntity,
  useRowChoiceExcludedLegalEntity,
} from "_shared/_views/tables/row-choice-excluded-legal-entity";
import { useRowChoiceRequiresCredentials } from "_shared/_views/tables/row-choice-requires-credentials";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import DebouncedTextField from "_shared/_views/text-input/debounced-text-field";
import Guid from "_shared/guids/guid";
import useInfiniteIndividualBookmarks from "marketplace/bookmarks/_hooks/use-infinite-individual-bookmarks";
import IndividualBookmarkQuery, {
  INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH,
  IndividualBookmarkOrderField,
} from "marketplace/bookmarks/individual-bookmarks/_api/queries/individual-bookmark-query";
import BookmarkedIndividualListItem from "marketplace/bookmarks/individual-bookmarks/bookmarked-individual-list-item";
import Individual from "marketplace/individuals/individual";
import {
  credentialsMissingReason,
  isKnownToLackCredentials,
} from "marketplace/individuals/profiles/professional-credentials";
import {
  createMRTColumnHelper,
  type MRT_TableOptions
} from "material-react-table";
import { useId, useMemo, useRef, useState } from "react";
// #endregion

const BookmarksTableRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  height: "100%",
  "& .BookmarkedIndividuals-tableContainer": {
    flex: 1,
  },
  "& .BookmarkedIndividuals-tablePaper": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
}));
const ErrorContent = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
}));

/** The name of a bookmarked person whom the server does not let the caller see. */
const NOT_VISIBLE_LABEL = "Not visible to you";

// #region Component Input Props
export interface BookmarkedIndividualsProps {
  selectedMemberUserIds?: Guid[];
  onSelected: (individual: Individual) => void;
};

const columnHelper = createMRTColumnHelper<BookmarkedIndividualListItem>();

/** A picker is read down its names, so it lists the bookmarks A to Z rather than newest first. */
function createQuery(search: string): IndividualBookmarkQuery {
  return new IndividualBookmarkQuery({
    query: search,
    orderBy: IndividualBookmarkOrderField.Name,
    orderDir: OrderDirection.Ascending,
  });
}

function getBookmarkName(bookmark: BookmarkedIndividualListItem): string {
  if (bookmark.individual) return bookmark.individual.fullName;
  return bookmark.isUnavailable ? NOT_VISIBLE_LABEL : "Unknown Individual";
}

// What the person does, under their name: their credentials, then their practice areas.
function getBookmarkDetails(bookmark: BookmarkedIndividualListItem): Array<string | undefined> {
  const profile = bookmark.individual?.profile;
  return [profile?.credentials, profile?.categories?.join(", ")];
}

export function BookmarkedIndividuals(props: Readonly<BookmarkedIndividualsProps>) {
  const {
    selectedMemberUserIds,
    onSelected
  } = props;

  // #region Hooks
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const requiresCredentials = useRowChoiceRequiresCredentials();
  const excludedLegalEntityId = useRowChoiceExcludedLegalEntity();
  const reasonIdPrefix = useId();
  const [search, setSearch] = useState("");
  // A stability guarantee, not defensive memoization: the hook starts again from the first page
  // whenever the query changes, so a new query on every render would never get past it.
  const query = useMemo(() => createQuery(search), [search]);
  const { bookmarks, isLoading, isInitialLoading, error, loadMore, retry } = useInfiniteIndividualBookmarks(query);
  useInfiniteScroll(loadMore, bookmarks.hasNextPage, isLoading, tableContainerRef);
  // The bookmarks this picker offers: all of them, less the firm it leaves out (#6091). A bookmark
  // whose person the caller may not see says nothing about their firm, so it stays.
  const offeredBookmarks = useMemo(
    () =>
      bookmarks.data.filter(
        (bookmark) => !isInExcludedLegalEntity(excludedLegalEntityId, bookmark.individual?.legalEntityId),
      ),
    [bookmarks.data, excludedLegalEntityId],
  );
  // The table may have nothing left to scroll once those are out, so keep loading past them.
  useLoadPastFilteredRows(
    offeredBookmarks.length < bookmarks.data.length,
    bookmarks.hasNextPage,
    isLoading,
    Boolean(error),
    loadMore,
    tableContainerRef,
  );
  // #endregion

  // #region Columns
  function isAlreadyChosen(bookmark: BookmarkedIndividualListItem): boolean {
    const individual = bookmark.individual;
    return Boolean(individual && selectedMemberUserIds?.some((id) => id.isEqualTo(individual.userId)));
  }

  // A team will not take a member whose profile lists no professional credentials (#5800). Each
  // bookmark carries the person's profile, so the picker can tell before the save is refused.
  function lacksCredentials(bookmark: BookmarkedIndividualListItem): boolean {
    return requiresCredentials && isKnownToLackCredentials(bookmark.individual);
  }

  function getUnavailableReason(bookmark: BookmarkedIndividualListItem | undefined): RowChoiceUnavailableReason | undefined {
    const individual = bookmark?.individual;
    if (!bookmark || !individual || isAlreadyChosen(bookmark) || !lacksCredentials(bookmark)) return undefined;
    return { id: `${reasonIdPrefix}-${individual.id.value}`, text: credentialsMissingReason };
  }

  // Undefined for a bookmark that cannot be chosen — somebody already chosen, somebody a team would
  // refuse, or a person the caller may not see, whose details the picker does not have — which is
  // what makes their row and its button say so. The comparison is by user id, as `selectedMemberUserIds` is: it used to be by the
  // bookmark's individual id, which never matched, so a person already on the team could be chosen
  // again. MRT asks for the props of the placeholder rows it draws while loading too, whose `original`
  // is an empty object, so a row with no record behind it is one that cannot be chosen, not a crash.
  function getChoice(bookmark: BookmarkedIndividualListItem | undefined): (() => void) | undefined {
    const individual = bookmark?.individual;
    if (!bookmark || !individual || isAlreadyChosen(bookmark) || lacksCredentials(bookmark)) return undefined;
    return () => onSelected(individual);
  }

  const columns = [
    columnHelper.accessor(getBookmarkName, {
      header: "Name",
      id: "name",
      Cell: ({ row }) => (
        <RowChoicePerson
          individual={row.original.individual}
          name={getBookmarkName(row.original)}
          details={getBookmarkDetails(row.original)}
          unavailableReason={getUnavailableReason(row.original)}
        />
      ),
    }),
    columnHelper.display({
      id: "choose",
      header: "",
      muiTableBodyCellProps: ROW_CHOICE_CELL_PROPS,
      Cell: ({ row }) => (
        <RowChoiceButton
          name={getBookmarkName(row.original)}
          onChoose={getChoice(row.original)}
          alreadyChosen={isAlreadyChosen(row.original)}
          describedBy={getUnavailableReason(row.original)?.id}
        />
      ),
    }),
  ];
  // #endregion

  // #region Table
  const tableOptions: MRT_TableOptions<BookmarkedIndividualListItem> = {
    // A picker: the row is a choice being made, not a record to act on. Removing a bookmark is the bookmarks page's job.
    ...ROW_CHOICE_TABLE_OPTIONS,
    columns,
    data: offeredBookmarks,
    muiTableBodyRowProps: ({ row }) => rowChoiceRowProps(getChoice(row.original)),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: error
      ? {
        color: "error",
        children: (
          <ErrorContent>
            Error loading data
            <Button color="inherit" size="small" onClick={retry}>Retry</Button>
          </ErrorContent>
        ),
      }
      : undefined,
    muiTableContainerProps: { className: "BookmarkedIndividuals-tableContainer", ref: tableContainerRef },
    muiTablePaperProps: { className: "BookmarkedIndividuals-tablePaper" },
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: Boolean(error),
      // The search box is outside the table, so the table cannot tell that a search left it empty.
      hasPageFilters: Boolean(query.query),
      isLoading: isInitialLoading,
      onRetry: retry,
      subject: "bookmarked individuals",
    }),
    state: {
      isLoading: isInitialLoading,
      showAlertBanner: Boolean(error),
      showProgressBars: isLoading && !isInitialLoading,
    },
  }
  // #endregion

  return (
    <BookmarksTableRoot>
      <DebouncedTextField
        label="Search your bookmarks"
        size="small"
        type="search"
        value={search}
        onDebouncedChange={setSearch}
        slotProps={{ htmlInput: { maxLength: INDIVIDUAL_BOOKMARK_QUERY_MAXIMUM_LENGTH } }}
      />
      {/* The picker's own search box, lent to the names it marks (#6590). */}
      <SearchMatchScope term={search}>
        <MRTNoMemo<BookmarkedIndividualListItem> options={tableOptions} />
      </SearchMatchScope>
    </BookmarksTableRoot>
  );
}
